import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import {
  RefreshCw,
  FileDown,
  FileSpreadsheet,
  Calendar,
  Filter,
  DollarSign,
  Wallet,
  TrendingUp,
  Users,
  Receipt,
  ArrowUpRight
} from "lucide-react";
import { getApiUrl } from "../../config/api";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

interface DashboardTotals {
  totalBilled: number;
  totalCollected: number;
  totalOutstanding: number;
  payrollExpenses: number;
  netPosition: number;
  studentsWithBalances: number;
}

interface ChartPoint {
  month: string;
  total: number;
}

interface DashboardData {
  filters: Record<string, any>;
  totals: DashboardTotals;
  charts: {
    monthlyIncome: ChartPoint[];
    monthlyPayroll: ChartPoint[];
    paymentMethods: Array<{ method: string; total: number }>;
    feeByClass: Array<{ className: string; total: number }>;
  };
  tables: {
    recentPayments: Array<any>;
    outstandingBalances: Array<any>;
    payrollSummary: Array<any>;
  };
}

const TERMS = ["First Term", "Second Term", "Third Term"];
const MONTHS = [
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" }
];

const PAYMENT_METHODS = ["Cash", "Mobile Money", "Bank Transfer", "Card", "Other"];
const STATUS_OPTIONS = ["paid", "partial", "unpaid"];

const COLORS = ["#0ea5a4", "#38bdf8", "#f59e0b", "#ef4444", "#6366f1", "#14b8a6"];

const FinancialReports: React.FC = () => {
  const token = localStorage.getItem("token");
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear.toString());
  const [term, setTerm] = useState("");
  const [month, setMonth] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [classId, setClassId] = useState("");
  const [status, setStatus] = useState("");
  const [classes, setClasses] = useState<any[]>([]);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useAutoDismiss(message, setMessage, null, 5000);

  const [paymentsSearch, setPaymentsSearch] = useState("");
  const [balancesSearch, setBalancesSearch] = useState("");
  const [payrollSearch, setPayrollSearch] = useState("");
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [balancesPage, setBalancesPage] = useState(1);
  const [payrollPage, setPayrollPage] = useState(1);
  const pageSize = 10;

  const formatCurrency = (value: number | undefined) =>
    new Intl.NumberFormat("en-ZM", { style: "currency", currency: "ZMW", maximumFractionDigits: 0 }).format(value || 0);

  const fetchClasses = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(getApiUrl("academics/classes"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClasses(res.data?.classes || res.data || []);
    } catch {
      setClasses([]);
    }
  }, [token]);

  const fetchDashboard = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setMessage(null);
    try {
      const params: Record<string, string> = { year };
      if (term) params.term = term;
      if (month) params.month = month;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (paymentMethod) params.paymentMethod = paymentMethod;
      if (classId) params.classId = classId;
      if (status) params.status = status;

      const res = await axios.get(getApiUrl("finance/reports/dashboard"), {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setDashboard(res.data);
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Failed to load financial reports.");
    } finally {
      setLoading(false);
    }
  }, [token, year, term, month, startDate, endDate, paymentMethod, classId, status]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const resetFilters = () => {
    setYear(currentYear.toString());
    setTerm("");
    setMonth("");
    setStartDate("");
    setEndDate("");
    setPaymentMethod("");
    setClassId("");
    setStatus("");
  };

  const monthlyComparison = useMemo(() => {
    if (!dashboard) return [];
    const incomeMap = new Map(dashboard.charts.monthlyIncome.map((row) => [row.month, row.total]));
    const payrollMap = new Map(dashboard.charts.monthlyPayroll.map((row) => [row.month, row.total]));
    const months = Array.from(new Set([...incomeMap.keys(), ...payrollMap.keys()])).sort();
    return months.map((m) => ({
      month: m,
      income: incomeMap.get(m) || 0,
      payroll: payrollMap.get(m) || 0
    }));
  }, [dashboard]);

  const payments = dashboard?.tables.recentPayments || [];
  const outstanding = dashboard?.tables.outstandingBalances || [];
  const payrolls = dashboard?.tables.payrollSummary || [];

  const filteredPayments = payments.filter((p) =>
    `${p.studentName} ${p.className} ${p.receiptNumber}`.toLowerCase().includes(paymentsSearch.toLowerCase())
  );
  const filteredBalances = outstanding.filter((b) =>
    `${b.studentName} ${b.className}`.toLowerCase().includes(balancesSearch.toLowerCase())
  );
  const filteredPayrolls = payrolls.filter((p) =>
    `${p.staffName} ${p.role} ${p.periodName}`.toLowerCase().includes(payrollSearch.toLowerCase())
  );

  const paged = (items: any[], page: number) => items.slice((page - 1) * pageSize, page * pageSize);

  const exportCsv = (filename: string, rows: any[], headers: string[]) => {
    if (rows.length === 0) return;
    const csv = [headers.join(",")]
      .concat(
        rows.map((row) =>
          headers
            .map((key) => `"${String(row[key] ?? "").replace(/\"/g, '"')}"`)
            .join(",")
        )
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const exportSummary = () => {
    if (!dashboard) return;
    exportCsv("financial_summary.csv", [dashboard.totals], [
      "totalBilled",
      "totalCollected",
      "totalOutstanding",
      "payrollExpenses",
      "netPosition",
      "studentsWithBalances"
    ]);
  };

  return (
    <div className="utopia-page">
      <div className="utopia-page-header">
        <div>
          <h1 className="utopia-page-title">Financial Reports</h1>
          <p className="page-subtitle">Track fees, payroll, collections, and cash flow in one place.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button className="utopia-btn-secondary flex items-center gap-2" onClick={fetchDashboard}>
            <RefreshCw size={16} /> Refresh
          </button>
          <button className="utopia-btn-secondary flex items-center gap-2" onClick={() => window.print()}>
            <FileDown size={16} /> Export PDF
          </button>
          <button className="utopia-btn-primary flex items-center gap-2" onClick={exportSummary}>
            <FileSpreadsheet size={16} /> Export Excel
          </button>
        </div>
      </div>

      <div className="utopia-card p-5 mb-6">
        <div className="flex items-center gap-2 text-slate-600 text-sm font-bold mb-4">
          <Filter size={16} /> Filters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Year</label>
            <input
              className="utopia-input"
              type="number"
              min="2000"
              max="2100"
              value={year}
              onChange={(e) => setYear(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Term</label>
            <select className="utopia-input" value={term} onChange={(e) => setTerm(e.target.value)}>
              <option value="">All Terms</option>
              {TERMS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Month</label>
            <select className="utopia-input" value={month} onChange={(e) => setMonth(e.target.value)}>
              <option value="">All Months</option>
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Payment Method</label>
            <select className="utopia-input" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
              <option value="">All Methods</option>
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Class / Grade</label>
            <select className="utopia-input" value={classId} onChange={(e) => setClassId(e.target.value)}>
              <option value="">All Classes</option>
              {classes.map((cls) => (
                <option key={cls.id} value={cls.id}>{cls.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Status</label>
            <select className="utopia-input" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All Status</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Start Date</label>
            <input className="utopia-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">End Date</label>
            <input className="utopia-input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <div className="flex items-end gap-2">
            <button className="utopia-btn-primary w-full" onClick={fetchDashboard}>Apply Filters</button>
          </div>
          <div className="flex items-end gap-2">
            <button className="utopia-btn-secondary w-full" onClick={resetFilters}>Reset Filters</button>
          </div>
        </div>
      </div>

      {message && (
        <div className="mb-6 p-3 rounded-xl bg-red-50 text-red-700 text-sm">{message}</div>
      )}

      {loading ? (
        <div className="utopia-card p-10 text-center text-slate-400">Loading financial dashboard...</div>
      ) : dashboard ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
            <div className="utopia-card p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                  <Receipt size={18} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Total Billed</p>
                  <p className="text-xl font-black text-slate-900">{formatCurrency(dashboard.totals.totalBilled)}</p>
                </div>
              </div>
            </div>
            <div className="utopia-card p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center text-teal-600">
                  <DollarSign size={18} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Total Collected</p>
                  <p className="text-xl font-black text-teal-600">{formatCurrency(dashboard.totals.totalCollected)}</p>
                </div>
              </div>
            </div>
            <div className="utopia-card p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
                  <Wallet size={18} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Outstanding Balance</p>
                  <p className="text-xl font-black text-amber-600">{formatCurrency(dashboard.totals.totalOutstanding)}</p>
                </div>
              </div>
            </div>
            <div className="utopia-card p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                  <Calendar size={18} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Payroll Expenses</p>
                  <p className="text-xl font-black text-indigo-600">{formatCurrency(dashboard.totals.payrollExpenses)}</p>
                </div>
              </div>
            </div>
            <div className="utopia-card p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600">
                  <TrendingUp size={18} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Net Position</p>
                  <p className={`text-xl font-black ${dashboard.totals.netPosition >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    {formatCurrency(dashboard.totals.netPosition)}
                  </p>
                </div>
              </div>
            </div>
            <div className="utopia-card p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
                  <Users size={18} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Students With Balances</p>
                  <p className="text-xl font-black text-blue-600">{dashboard.totals.studentsWithBalances}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
            <div className="utopia-card p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold">Monthly Income Trend</h3>
              </div>
              {dashboard.charts.monthlyIncome.length === 0 ? (
                <div className="text-sm text-slate-400">No fee payments recorded for this period.</div>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dashboard.charts.monthlyIncome}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="total" stroke="#0ea5a4" strokeWidth={3} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="utopia-card p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold">Monthly Payroll Expenses</h3>
              </div>
              {dashboard.charts.monthlyPayroll.length === 0 ? (
                <div className="text-sm text-slate-400">No payroll expenses recorded for this period.</div>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dashboard.charts.monthlyPayroll}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="total" fill="#6366f1" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
            <div className="utopia-card p-4">
              <h3 className="text-lg font-bold mb-3">Fees Collected vs Outstanding</h3>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      dataKey="value"
                      data={[
                        { name: "Collected", value: dashboard.totals.totalCollected },
                        { name: "Outstanding", value: dashboard.totals.totalOutstanding }
                      ]}
                      innerRadius={40}
                      outerRadius={70}
                    >
                      <Cell fill="#0ea5a4" />
                      <Cell fill="#f59e0b" />
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="utopia-card p-4">
              <h3 className="text-lg font-bold mb-3">Income vs Payroll</h3>
              {monthlyComparison.length === 0 ? (
                <div className="text-sm text-slate-400">No income or payroll data for this period.</div>
              ) : (
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyComparison}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="income" fill="#14b8a6" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="payroll" fill="#ef4444" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            <div className="utopia-card p-4">
              <h3 className="text-lg font-bold mb-3">Payment Method Breakdown</h3>
              {dashboard.charts.paymentMethods.length === 0 ? (
                <div className="text-sm text-slate-400">No payment method data available.</div>
              ) : (
                <div className="space-y-2">
                  {dashboard.charts.paymentMethods.map((row, idx) => (
                    <div key={row.method} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                        {row.method}
                      </div>
                      <span className="font-semibold">{formatCurrency(row.total)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
            <div className="utopia-card p-4">
              <h3 className="text-lg font-bold mb-3">Fee Collection by Class</h3>
              {dashboard.charts.feeByClass.length === 0 ? (
                <div className="text-sm text-slate-400">No class breakdown available.</div>
              ) : (
                <div className="space-y-2">
                  {dashboard.charts.feeByClass.map((row) => (
                    <div key={row.className} className="flex items-center justify-between text-sm">
                      <span>{row.className}</span>
                      <span className="font-semibold">{formatCurrency(row.total)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="utopia-card p-4">
              <h3 className="text-lg font-bold mb-3">Quick Actions</h3>
              <div className="space-y-3">
                <a href="/finance/feessummary" className="utopia-btn-secondary w-full flex items-center justify-between">
                  Fees & Payments <ArrowUpRight size={16} />
                </a>
                <a href="/payroll-management" className="utopia-btn-secondary w-full flex items-center justify-between">
                  Payroll Management <ArrowUpRight size={16} />
                </a>
                <button className="utopia-btn-primary w-full" onClick={() => window.print()}>
                  Print Financial Report
                </button>
              </div>
            </div>
          </div>

          <div className="utopia-card p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold">Recent Payments</h3>
              <div className="flex items-center gap-2">
                <input
                  className="utopia-input h-9"
                  placeholder="Search payments..."
                  value={paymentsSearch}
                  onChange={(e) => setPaymentsSearch(e.target.value)}
                />
                <button
                  className="utopia-btn-secondary h-9"
                  onClick={() => exportCsv("recent_payments.csv", filteredPayments, ["paidAt", "studentName", "className", "amount", "paymentMethod", "receiptNumber", "recordedBy"])}
                >
                  Export
                </button>
              </div>
            </div>
            {filteredPayments.length === 0 ? (
              <div className="text-sm text-slate-400">No fee payments recorded for this period. Go to Fees & Payments to record transactions.</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-500">
                        <th className="py-2">Date</th>
                        <th className="py-2">Student</th>
                        <th className="py-2">Class</th>
                        <th className="py-2">Amount</th>
                        <th className="py-2">Method</th>
                        <th className="py-2">Receipt</th>
                        <th className="py-2">Recorded By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paged(filteredPayments, paymentsPage).map((row) => (
                        <tr key={row.id} className="border-t border-slate-100">
                          <td className="py-2">{row.paidAt ? new Date(row.paidAt).toLocaleDateString() : "-"}</td>
                          <td className="py-2 font-semibold">{row.studentName}</td>
                          <td className="py-2">{row.className || "-"}</td>
                          <td className="py-2">{formatCurrency(row.amount)}</td>
                          <td className="py-2">{row.paymentMethod || "-"}</td>
                          <td className="py-2">{row.receiptNumber || "-"}</td>
                          <td className="py-2">{row.recordedBy || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
                  <span>Showing {paged(filteredPayments, paymentsPage).length} of {filteredPayments.length}</span>
                  <div className="flex gap-2">
                    <button className="utopia-btn-secondary h-8" disabled={paymentsPage === 1} onClick={() => setPaymentsPage((p) => p - 1)}>Prev</button>
                    <button className="utopia-btn-secondary h-8" disabled={paymentsPage >= Math.ceil(filteredPayments.length / pageSize)} onClick={() => setPaymentsPage((p) => p + 1)}>Next</button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="utopia-card p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold">Outstanding Balances</h3>
              <div className="flex items-center gap-2">
                <input
                  className="utopia-input h-9"
                  placeholder="Search balances..."
                  value={balancesSearch}
                  onChange={(e) => setBalancesSearch(e.target.value)}
                />
                <button
                  className="utopia-btn-secondary h-9"
                  onClick={() => exportCsv("outstanding_balances.csv", filteredBalances, ["studentName", "className", "totalAmount", "paid", "balance", "status"])}
                >
                  Export
                </button>
              </div>
            </div>
            {filteredBalances.length === 0 ? (
              <div className="text-sm text-slate-400">No outstanding balances for this period.</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-500">
                        <th className="py-2">Student</th>
                        <th className="py-2">Class</th>
                        <th className="py-2">Total Billed</th>
                        <th className="py-2">Paid</th>
                        <th className="py-2">Balance</th>
                        <th className="py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paged(filteredBalances, balancesPage).map((row, idx) => (
                        <tr key={`${row.studentId}-${idx}`} className="border-t border-slate-100">
                          <td className="py-2 font-semibold">{row.studentName}</td>
                          <td className="py-2">{row.className || "-"}</td>
                          <td className="py-2">{formatCurrency(row.totalAmount)}</td>
                          <td className="py-2">{formatCurrency(row.paid)}</td>
                          <td className="py-2 text-amber-600 font-semibold">{formatCurrency(row.balance)}</td>
                          <td className="py-2">{row.status || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
                  <span>Showing {paged(filteredBalances, balancesPage).length} of {filteredBalances.length}</span>
                  <div className="flex gap-2">
                    <button className="utopia-btn-secondary h-8" disabled={balancesPage === 1} onClick={() => setBalancesPage((p) => p - 1)}>Prev</button>
                    <button className="utopia-btn-secondary h-8" disabled={balancesPage >= Math.ceil(filteredBalances.length / pageSize)} onClick={() => setBalancesPage((p) => p + 1)}>Next</button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="utopia-card p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold">Payroll Summary</h3>
              <div className="flex items-center gap-2">
                <input
                  className="utopia-input h-9"
                  placeholder="Search payroll..."
                  value={payrollSearch}
                  onChange={(e) => setPayrollSearch(e.target.value)}
                />
                <button
                  className="utopia-btn-secondary h-9"
                  onClick={() => exportCsv("payroll_summary.csv", filteredPayrolls, ["staffName", "role", "grossSalary", "totalDeductions", "netSalary", "periodName"])}
                >
                  Export
                </button>
              </div>
            </div>
            {filteredPayrolls.length === 0 ? (
              <div className="text-sm text-slate-400">No payroll records found for this period.</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-500">
                        <th className="py-2">Staff</th>
                        <th className="py-2">Role</th>
                        <th className="py-2">Gross Pay</th>
                        <th className="py-2">Deductions</th>
                        <th className="py-2">Net Pay</th>
                        <th className="py-2">Period</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paged(filteredPayrolls, payrollPage).map((row, idx) => (
                        <tr key={`${row.id}-${idx}`} className="border-t border-slate-100">
                          <td className="py-2 font-semibold">{row.staffName}</td>
                          <td className="py-2">{row.role}</td>
                          <td className="py-2">{formatCurrency(row.grossSalary)}</td>
                          <td className="py-2">{formatCurrency(row.totalDeductions)}</td>
                          <td className="py-2 font-semibold">{formatCurrency(row.netSalary)}</td>
                          <td className="py-2">{row.periodName || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
                  <span>Showing {paged(filteredPayrolls, payrollPage).length} of {filteredPayrolls.length}</span>
                  <div className="flex gap-2">
                    <button className="utopia-btn-secondary h-8" disabled={payrollPage === 1} onClick={() => setPayrollPage((p) => p - 1)}>Prev</button>
                    <button className="utopia-btn-secondary h-8" disabled={payrollPage >= Math.ceil(filteredPayrolls.length / pageSize)} onClick={() => setPayrollPage((p) => p + 1)}>Next</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      ) : (
        <div className="utopia-card p-10 text-center text-slate-400">No summary available.</div>
      )}
    </div>
  );
};

export default FinancialReports;
